import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Check, ArrowRight } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

const KEY = "gs_banner_dismissed_until";

export default function Banner({ banner }) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!banner?.enabled) return;
    const until = parseInt(localStorage.getItem(KEY) || "0", 10);
    if (Date.now() < until) return;
    const delay = (Number(banner.show_delay) || 5) * 1000;
    const t = setTimeout(() => setOpen(true), delay);
    return () => clearTimeout(t);
  }, [banner]);

  if (!banner?.enabled) return null;

  const dismiss = () => {
    const days = Number(banner.dismiss_days) || 7;
    localStorage.setItem(KEY, String(Date.now() + days * 86400000));
    setOpen(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/subscribers", { email });
      setDone(true);
      const days = Number(banner.dismiss_days) || 7;
      localStorage.setItem(KEY, String(Date.now() + days * 86400000));
    } catch (err) {
      const { toast } = await import("sonner");
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSubmitting(false); }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div className="fixed inset-0 z-[80] flex items-center justify-center bg-obsidian/80 p-4 backdrop-blur-sm"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} data-testid="popup-banner">
          <motion.div className="relative w-full max-w-md overflow-hidden border border-gold/25 bg-obsidian"
            initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 30, opacity: 0 }} transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}>
            <button onClick={dismiss} data-testid="banner-close" className="absolute right-4 top-4 z-10 text-softgrey transition-colors hover:text-warmwhite"><X size={20} /></button>
            <div className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full" style={{ background: "radial-gradient(circle, rgba(201,162,39,0.14) 0%, transparent 70%)" }} />
            <div className="relative p-9">
              {done ? (
                <div className="text-center" data-testid="banner-success">
                  <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full border border-gold"><Check className="text-gold" /></span>
                  <h3 className="mt-6 font-serif text-2xl text-warmwhite">{banner.success_title || "You're On The List"}</h3>
                  <p className="mt-3 font-sans text-sm leading-relaxed text-softgrey">{banner.success_text || "We'll notify you as soon as a new deal drops."}</p>
                  <button onClick={() => { setOpen(false); navigate("/opportunities"); }} className="mt-7 inline-flex items-center gap-2 border border-gold/40 px-6 py-3 font-sans text-sm text-warmwhite hover:bg-gold/10">
                    Explore Opportunities <ArrowRight size={15} className="text-gold" />
                  </button>
                </div>
              ) : (
                <>
                  {banner.badge && (
                    <span className="inline-block border border-gold/40 px-3 py-1 font-sans text-[10px] tracking-luxe uppercase text-champagne">{banner.badge}</span>
                  )}
                  <h3 className="mt-5 font-serif text-2xl leading-snug text-warmwhite">{banner.headline}</h3>
                  <p className="mt-3 font-sans text-sm leading-relaxed text-softgrey">{banner.supporting_text}</p>
                  <form onSubmit={submit} className="mt-6 space-y-3">
                    <input type="email" required placeholder="Your email address" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="banner-email"
                      className="w-full border-b border-gold/25 bg-transparent py-2.5 font-sans text-sm text-warmwhite outline-none placeholder:text-softgrey/50 focus:border-gold" />
                    <button type="submit" disabled={submitting} data-testid="banner-cta"
                      className="group flex w-full items-center justify-center gap-2 bg-gold py-3.5 font-sans text-sm font-medium tracking-wide text-obsidian transition-all hover:brightness-110 disabled:opacity-60">
                      {submitting ? "Submitting…" : (banner.cta_label || "Get Early Access")} <ArrowRight size={16} className="transition-transform group-hover:translate-x-0.5" />
                    </button>
                  </form>
                </>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
